<template>
  <v-card dark>
    <v-card-title primary-title>
      <v-tooltip bottom>
        <CoverImageUpload
          slot="activator"
          :src="opportunity.coverImage"
          :path="`/admin/opportunities/${opportunity.id}/cover_image`"
        />
        <span>Click to upload cover image</span>
      </v-tooltip>
      <div>
        <div class="headline">{{ opportunity.name }}</div>
        <span class="grey--text">{{ opportunity.organization.name }}</span>
      </div>
      <v-spacer />
      <v-tooltip bottom>
        <v-btn slot="activator" @click="$emit('cancel')" icon><v-icon>mdi-close</v-icon></v-btn>
        <span>Click to close without saving</span>
      </v-tooltip>
    </v-card-title>

    <v-alert transition="scale-transition" v-bind:value="opportunity.errors">
      <ul>
        <li v-for="(message, i) in opportunity.errors" :key="`error-${i}`">{{ message }}</li>
      </ul>
    </v-alert>

    <v-card-text>
      <v-form v-model="valid" @submit.prevent="submit">
        <v-text-field label="Name" v-model="opportunity.name" :rules="rules.name" required />

        <!-- v-if delays rendering the component until the selected item exists and therefore renders as selected -->
        <v-select
          label="Organization"
          v-model="opportunity.organization.id"
          required
          :rules="rules.organization"
          :items="organizations"
          v-if="organizations.length > 0"
          item-text="name"
          item-value="id"
        />

        <v-combobox
          label="Set"
          v-model="opportunity.set"
          chips
          :items="sets"
          placeholder="Used to group opportunities together for analytics"
        />

        <v-select
          label="Type"
          v-model="opportunity.type"
          :items="[
            { text: 'Job', value: 'job' },
            { text: 'Bursary', value: 'bursary' },
            { text: 'Event', value: 'event' },
          ]"
          required
        />

        <date-picker
          label="Opening Date"
          v-model="opportunity.opensAt"
          required
          :rules="rules.opensAt"
          append-icon="mdi-calendar-today"
          :max="opportunity.closesAt"
        />

        <date-picker
          label="Closing Date"
          v-model="opportunity.closesAt"
          required
          :rules="rules.closesAt"
          append-icon="mdi-calendar"
          type="datetime"
          :max="opportunity.commencesAt"
          :min="opportunity.opensAt"
        />

        <date-picker
          label="Commencement Date"
          v-model="opportunity.commencesAt"
          :rules="rules.commencesAt"
          append-icon="mdi-calendar-star"
          :min="opportunity.closesAt"
        />

        <v-select
          v-model="opportunity.remuneration.currency"
          label="Remuneration Currency"
          :items="currencies"
        />
        <v-text-field
          v-model="opportunity.remuneration.amount"
          label="Remuneration Amount"
          type="number"
        />
        <v-select
          v-model="opportunity.remuneration.frequency"
          label="Remuneration Interval"
          :items="frequencies"
        />
        <v-switch
          :label="
            `Remuneration ${opportunity.remuneration.disclosed ? 'Disclosed' : 'Undisclosed'}`
          "
          v-model="opportunity.remuneration.disclosed"
        />

        <v-select
          v-model="opportunity.behavioralCompetencies"
          label="Personal Traits"
          chips
          :items="behavioralCompetencies"
          v-if="behavioralCompetencies.length > 0"
          item-text="name"
          item-value="id"
          return-object
          multiple
          deletable-chips
          persistent-hint
          hint="Valuable traits for a candidate to have"
        />

        <v-combobox
          v-model="opportunity.technicalCompetencies"
          label="Technical Competencies"
          chips
          multiple
          :items="technicalCompetencies"
          persistent-hint
          hint="Essential skills required to succeed in the opportunity"
        />
        <v-combobox
          v-model="opportunity.professionalTools"
          label="Professional Tools"
          chips
          multiple
          :items="[]"
          append-icon=""
          persistent-hint
          hint="Tools the candidate will be expected to make use of"
        />

        <v-divider />

        <v-textarea v-model="opportunity.summary" label="Summary" required auto-grow />

        <v-text-field
          v-model="opportunity.blurb"
          label="Short Blurb"
          :rules="rules.blurb"
          counter="256"
        />

        <v-text-field
          v-model="opportunity.video"
          label="Promotional Video"
          clearable
          hint="Provide the full embed URL to the YouTube or Vimeo page"
        />

        <v-combobox
          chips
          multiple
          v-model="opportunity.perks"
          label="Perks"
          :items="[]"
          append-icon=""
        />

        <v-combobox
          chips
          multiple
          v-model="opportunity.offerings"
          label="Key Offerings"
          :items="[]"
          append-icon=""
        />
      </v-form>

      <v-alert outline type="warning" :value="!opportunity.organization.publishedAt">
        The Organization is not published, therefore the opportunity is not published
      </v-alert>
      <v-alert outline color="info" type="info" :value="opportunity.published">
        Opportunity published at
        {{ new Date(opportunity.publishedAt) | date('h:mm a, MMMM do yyyy') }}
        ({{ new Date(opportunity.publishedAt) | inWords({ addSuffix: true }) }})
      </v-alert>
      <v-alert outline color="info" type="info" :value="!opportunity.publishedAt">
        The Opportunity is not published
      </v-alert>
      <v-alert outline type="warning" :value="unpublish">
        Updating the opportunity will unpublish it.
      </v-alert>
      <v-switch label="Unpublish" v-model="unpublish" v-if="opportunity.published" />
    </v-card-text>

    <v-card-actions>
      <v-spacer />
      <v-btn flat @click="$emit('cancel')" color="grey">
        Cancel
      </v-btn>
      <v-btn @click="submit" color="primary">
        Update opportunity
      </v-btn>
      <v-btn flat @click="$emit('publish')" v-if="!opportunity.published">
        <v-icon left>mdi-earth</v-icon> Publish
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
// Components
import CoverImageUpload from '@/components/CoverImageUpload'

// Data
import currency from '@/data/currencies'
import technicalCompetencies from '@/data/technical_competencies'

// Helpers
import { mapGetters } from 'vuex'

// Vue Component
export default {
  components: { CoverImageUpload },
  created() {
    this.$store.dispatch('admin/organizations/fetchAll')
    this.$store.dispatch('behavioralCompetencies/fetchAll')
    this.$http
      .get(`admin/organizations/${this.opportunity.organization.id}/sets`)
      .then(({ data }) => (this.sets = data))
  },
  data() {
    return {
      publishing: false,
      unpublish: false,
      // Validations
      valid: true,
      rules: {
        name: [v => !!v || 'must be specified'],
        organization: [v => !!v || 'must be selected'],
        opensAt: [v => !!v || 'must be specified'],
        closesAt: [v => !!v || 'must be specified'],
        commencesAt: [],
        blurb: [v => !v || v.length < 256 || 'must be shorter than 256 characters'],
      },
      // Options
      frequencies: [
        { text: 'Yearly', value: 1 },
        { text: 'Monthly', value: 12 },
        { text: 'Weekly', value: 52 },
      ],
      sets: [],
      technicalCompetencies,
    }
  },
  computed: {
    ...mapGetters({
      organizations: 'admin/organizations/all',
      behavioralCompetencies: 'behavioralCompetencies/all',
    }),
    opportunity() {
      return this.$store.getters['admin/opportunities/find']()
    },
    currencies() {
      return Object.values(currency).map(c => {
        return { text: c.name, value: c.iso.code }
      })
    },
  },
  methods: {
    submit() {
      if (this.valid) {
        if (this.unpublish) Object.assign(this.opportunity, { publishedAt: null })
        this.$emit('update')
      }
    },
  },
}
</script>
